import React, { useContext, useEffect, useMemo, useState } from 'react';
import { BellRing, Calendar, Pencil, Pin, Save, Send, ShieldAlert, Trash2, X } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import api from '../utils/api';
import { AuthContext } from '../contexts/AuthContext';

const initialForm = {
    title: '',
    body: '',
    priority: 'info',
    targetRole: 'shipment_staff',
    isPinned: false,
    requiresAck: false,
    startAt: '',
    endAt: '',
};

const priorityStyles = {
    info: 'bg-blue-500/10 text-blue-300 border-blue-500/20',
    warning: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
    urgent: 'bg-red-500/10 text-red-300 border-red-500/20',
};

const formatDateTimeInput = (value) => {
    if (!value) return '';
    const normalized = String(value).replace(' ', 'T');
    return normalized.slice(0, 16);
};

const MobileAnnouncements = () => {
    const { user } = useContext(AuthContext);
    const [announcements, setAnnouncements] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [editingId, setEditingId] = useState(null);

    const isAdmin = user?.role === 'admin';

    const fetchAnnouncements = async () => {
        setIsLoading(true);
        try {
            const { data } = await api.get('/mobile/admin/announcements');
            setAnnouncements(data);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Mobil duyurular alınamadı.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isAdmin) {
            fetchAnnouncements();
        }
    }, [isAdmin]);

    const sortedAnnouncements = useMemo(
        () => [...announcements].sort((left, right) => Number(right.isPinned) - Number(left.isPinned)),
        [announcements]
    );

    if (!isAdmin) {
        return <Navigate to="/dashboard" />;
    }

    const resetForm = () => {
        setEditingId(null);
        setForm(initialForm);
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setForm({
            title: item.title || '',
            body: item.body || '',
            priority: item.priority || 'info',
            targetRole: item.targetRole || 'shipment_staff',
            isPinned: Boolean(item.isPinned),
            requiresAck: Boolean(item.requiresAck),
            startAt: formatDateTimeInput(item.startAt),
            endAt: formatDateTimeInput(item.endAt),
        });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu mobil duyuruyu silmek istediğinize emin misiniz?')) return;

        try {
            await api.delete(`/mobile/admin/announcements/${id}`);
            toast.success('Mobil duyuru silindi.');
            if (editingId === id) resetForm();
            fetchAnnouncements();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Duyuru silinemedi.');
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        try {
            const payload = {
                ...form,
                startAt: form.startAt || null,
                endAt: form.endAt || null,
            };

            if (editingId) {
                await api.put(`/mobile/admin/announcements/${editingId}`, payload);
                toast.success('Mobil duyuru güncellendi.');
            } else {
                await api.post('/mobile/admin/announcements', payload);
                toast.success('Mobil duyuru oluşturuldu.');
            }

            resetForm();
            fetchAnnouncements();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Duyuru kaydedilemedi.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Layout>
            <div className="flex flex-col xl:flex-row gap-8">
                <section className="xl:w-[420px] glass border border-slate-700/50 rounded-2xl p-6 h-fit">
                    <div className="flex items-center justify-between gap-3 mb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                                <BellRing size={24} className="text-primary-400" />
                                Mobil Mesajlar
                            </h1>
                            <p className="text-slate-400 text-sm mt-2">Saha personelinin Android ana sayfasında görünecek duyurular.</p>
                        </div>
                        {editingId ? (
                            <button
                                onClick={resetForm}
                                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                                title="Yeni duyuruya dön"
                            >
                                <X size={18} />
                            </button>
                        ) : null}
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">Başlık</label>
                            <input
                                value={form.title}
                                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                                className="w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-primary-500"
                                placeholder="Örn: Bugün çıkışlar 16:00'dan önce girilsin"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">Mesaj</label>
                            <textarea
                                value={form.body}
                                onChange={(event) => setForm((prev) => ({ ...prev, body: event.target.value }))}
                                rows={5}
                                className="w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-primary-500"
                                placeholder="Saha personelinin göreceği mesajı yazın..."
                                required
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Öncelik</label>
                                <select
                                    value={form.priority}
                                    onChange={(event) => setForm((prev) => ({ ...prev, priority: event.target.value }))}
                                    className="w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-primary-500"
                                >
                                    <option value="info">Bilgi</option>
                                    <option value="warning">Uyarı</option>
                                    <option value="urgent">Acil</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Hedef Rol</label>
                                <select
                                    value={form.targetRole}
                                    onChange={(event) => setForm((prev) => ({ ...prev, targetRole: event.target.value }))}
                                    className="w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-primary-500"
                                >
                                    <option value="shipment_staff">Saha Personeli</option>
                                    <option value="all">Tüm Mobil Kullanıcılar</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Başlangıç</label>
                                <input
                                    type="datetime-local"
                                    value={form.startAt}
                                    onChange={(event) => setForm((prev) => ({ ...prev, startAt: event.target.value }))}
                                    className="w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-primary-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Bitiş</label>
                                <input
                                    type="datetime-local"
                                    value={form.endAt}
                                    onChange={(event) => setForm((prev) => ({ ...prev, endAt: event.target.value }))}
                                    className="w-full px-4 py-3 bg-slate-900/60 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-primary-500"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <label className="flex items-center gap-3 text-sm text-slate-200 bg-slate-900/40 border border-slate-700 rounded-xl px-4 py-3">
                                <input
                                    type="checkbox"
                                    checked={form.isPinned}
                                    onChange={(event) => setForm((prev) => ({ ...prev, isPinned: event.target.checked }))}
                                    className="accent-primary-500"
                                />
                                Üstte sabitle
                            </label>

                            <label className="flex items-center gap-3 text-sm text-slate-200 bg-slate-900/40 border border-slate-700 rounded-xl px-4 py-3">
                                <input
                                    type="checkbox"
                                    checked={form.requiresAck}
                                    onChange={(event) => setForm((prev) => ({ ...prev, requiresAck: event.target.checked }))}
                                    className="accent-primary-500"
                                />
                                Okundu onayı iste
                            </label>
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-500 text-white font-bold py-3 rounded-xl transition-all disabled:opacity-50"
                        >
                            {editingId ? <Save size={18} /> : <Send size={18} />}
                            {editingId ? 'Duyuruyu Güncelle' : 'Duyuruyu Yayınla'}
                        </button>
                    </form>
                </section>

                <section className="flex-1 glass border border-slate-700/50 rounded-2xl overflow-hidden">
                    <div className="p-6 border-b border-slate-700/50 flex items-center justify-between gap-3">
                        <div>
                            <h2 className="text-xl font-bold text-white">Yayınlanan Duyurular</h2>
                            <p className="text-slate-400 text-sm mt-2">Android ana sayfasında gösterilen aktif ve geçmiş mesajlar.</p>
                        </div>
                        <button
                            onClick={fetchAnnouncements}
                            className="px-4 py-2 rounded-lg bg-slate-800 text-slate-200 hover:bg-slate-700 transition-colors"
                        >
                            Yenile
                        </button>
                    </div>

                    <div className="p-6 space-y-4">
                        {isLoading ? (
                            <div className="text-slate-400">Duyurular yükleniyor...</div>
                        ) : sortedAnnouncements.length === 0 ? (
                            <div className="border border-dashed border-slate-700 rounded-2xl p-10 text-center text-slate-400">
                                Henüz mobil duyuru oluşturulmamış.
                            </div>
                        ) : (
                            sortedAnnouncements.map((item) => (
                                <article key={item.id} className="border border-slate-700/60 rounded-2xl p-5 bg-slate-900/30">
                                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                                        <div className="space-y-3">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <h3 className="text-lg font-bold text-white">{item.title}</h3>
                                                {item.isPinned ? (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-primary-500/30 bg-primary-500/10 text-primary-300 text-xs font-semibold">
                                                        <Pin size={12} />
                                                        Sabit
                                                    </span>
                                                ) : null}
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-semibold ${priorityStyles[item.priority] || priorityStyles.info}`}>
                                                    {item.priority === 'urgent' ? 'Acil' : item.priority === 'warning' ? 'Uyarı' : 'Bilgi'}
                                                </span>
                                                {item.requiresAck ? (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-yellow-500/20 bg-yellow-500/10 text-yellow-300 text-xs font-semibold">
                                                        <ShieldAlert size={12} />
                                                        Onay İster
                                                    </span>
                                                ) : null}
                                            </div>

                                            <p className="text-slate-300 whitespace-pre-wrap leading-7">{item.body}</p>

                                            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
                                                <span className="inline-flex items-center gap-1.5">
                                                    <Calendar size={14} />
                                                    Başlangıç: {item.startAt ? new Date(item.startAt).toLocaleString('tr-TR') : 'Hemen'}
                                                </span>
                                                <span>Bitiş: {item.endAt ? new Date(item.endAt).toLocaleString('tr-TR') : 'Süresiz'}</span>
                                                <span>Hedef: {item.targetRole === 'all' ? 'Tüm Mobil Kullanıcılar' : 'Saha Personeli'}</span>
                                                <span>Okuyan: {item.readCount || 0}</span>
                                                <span>Onaylayan: {item.acknowledgedCount || 0}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleEdit(item)}
                                                className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
                                                title="Duyuruyu düzenle"
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(item.id)}
                                                className="p-2 rounded-lg text-slate-300 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                                title="Duyuruyu sil"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            ))
                        )}
                    </div>
                </section>
            </div>
        </Layout>
    );
};

export default MobileAnnouncements;
